import type { ReactNode, ReactElement } from "react"
import React, { useMemo, useContext, createContext, useState } from "react"
import { AccountId as CaipAccountId } from "caip"

import type { AccountId, ChainId, Governor } from "query/graphql"
import { useCreateProposalContentQuery } from "query/graphql"
import { accountIdToChainId } from "web3/helpers/transformers"
import { getChainIdParams } from "web3/helpers/chainId"
import { useTokenBalances } from "common/hooks/useTokenBalances"
import type { TokenBalances } from "common/types/tokenBalances"
import { useChainReference } from "web3/hooks/useChainReference"
import { getCustomProposalSourceTreasuryAddress } from "governance/helpers/treasury"

type FormState = "pending" | "success" | "error"

type CreateProposalContextValues = {
  governance: Governor
  governanceChainId: ChainId
  governanceId: AccountId
  governanceIds?: AccountId[]
  isCorrectChain: boolean
  tokenBalances: TokenBalances | undefined
  treasuryAddress: string
  walletChainReference?: number
  isArbitrum: boolean
  setFormState: (formState: FormState) => void
  formState: string
}

const CreateProposalContext = createContext<
  CreateProposalContextValues | undefined
>(undefined)

type CreateProposalProviderProps = {
  children: ReactNode
  governanceId: AccountId
  governanceIds?: AccountId[]
  governanceSlug: string
}

export const CreateProposalProvider = ({
  children,
  governanceId,
  governanceIds,
  governanceSlug,
}: CreateProposalProviderProps): null | ReactElement => {
  const governanceChainId = accountIdToChainId(governanceId)
  const { reference: governanceChainReference } =
    getChainIdParams(governanceChainId)

  const [formState, setFormState] = useState<FormState>("pending")

  const chainReference = useChainReference()
  const { data, isLoading } = useCreateProposalContentQuery({
    input: {
      id: governanceId,
    },
  })

  const isArbitrum = governanceSlug === "arbitrum"

  const treasuryAddress = useMemo(() => {
    if (!data) return ""

    const customTreasuryAddress = getCustomProposalSourceTreasuryAddress({
      slug: governanceSlug,
    })

    if (customTreasuryAddress) return customTreasuryAddress

    const treasuryAddress = data.governor?.timelockId
      ? CaipAccountId.parse(data.governor.timelockId).address
      : data.governor?.contracts?.governor?.address

    return treasuryAddress
  }, [data, governanceSlug])
  const { tokenBalances } = useTokenBalances(
    governanceChainReference,
    treasuryAddress,
  )

  const value = useMemo(() => {
    if (!data) return

    const walletChainReference = chainReference
    const isCorrectChain = governanceChainReference === walletChainReference

    return {
      governance: data.governor as Governor,
      governanceChainId,
      governanceId,
      governanceIds,
      isCorrectChain,
      tokenBalances,
      treasuryAddress,
      walletChainReference,
      isArbitrum,
      setFormState,
      formState,
    }
  }, [
    chainReference,
    data,
    governanceId,
    governanceIds,
    governanceChainId,
    governanceChainReference,
    treasuryAddress,
    tokenBalances,
    isArbitrum,
    setFormState,
    formState,
  ])

  if (isLoading) return null

  if (!data) return null

  return (
    <CreateProposalContext.Provider value={value}>
      {children}
    </CreateProposalContext.Provider>
  )
}

export const useCreateProposal = (): CreateProposalContextValues => {
  const context = useContext(CreateProposalContext)

  if (!context)
    throw new Error(
      "CreateProposalContext must be used within CreateProposalProvider",
    )

  return context
}
